import { ApiService } from '@services/ApiService'
import { intentRegistry } from '../config/IntentRegistry'
import type {
  ExtractedEntity,
  IntentContext,
  IntentDefinition,
  IntentResult,
} from '../types/intent'

const DefaultOpenAIModel = 'gpt-4'

// Validate entities according to intent definition
const validateEntities = (
  rawEntities: Record<string, any>,
  intentDef: IntentDefinition
): Record<string, ExtractedEntity> => {
  const validatedEntities: Record<string, ExtractedEntity> = {}

  // Check each entity in intent definition
  for (const entityDef of intentDef.entities) {
    const rawEntity = rawEntities[entityDef.name]

    if (rawEntity) {
      // Validate entity value
      let isValid = true
      if (entityDef.validation) {
        isValid = entityDef.validation(rawEntity.value)
      }

      if (isValid) {
        validatedEntities[entityDef.name] = {
          name: entityDef.name,
          value: rawEntity.value,
          confidence: rawEntity.confidence || 0.8,
          type: entityDef.type,
        }
      }
    } else if (entityDef.required) {
      // Entity required but not found
      console.warn(`Required entity missing: ${entityDef.name}`)
    }
  }

  // Add entities not defined but detected
  for (const [entityName, entityValue] of Object.entries(rawEntities)) {
    if (!validatedEntities[entityName] && entityValue) {
      validatedEntities[entityName] = {
        name: entityName,
        value: entityValue.value || entityValue,
        confidence: entityValue.confidence || 0.6,
        type: entityValue.type || 'string',
      }
    }
  }

  return validatedEntities
}

export const analyzeWithOpenAI = async (userMessage: string) => {
  // Create processing logic similar to the component
  const intents = intentRegistry.getAllIntentDescriptions()
  const systemPrompt = `You are an AI assistant that extracts the user’s intent and parameters from natural language queries..
## Available intents:
${intents}

## Rules:

Analyze the user message and extract:
- Extract entity values according to their description, not raw text.
- Always output JSON only.
- Only use intents from the provided list.
- If no valid intent matches, return: { "intentName": "unknown", "entities": {} }

Respond ONLY in JSON format:
{
  "intent": "intent_name",
  "entities": {
    "entityName": {
      "value": "extracted_value",
      "confidence": 0.95,
      "type": "entity_type"
    }
  },
  "confidence": 0.95
}`

  // const response = await fetch('https://api.openai.com/v1/chat/completions', {
  //   method: 'POST',
  //   headers: {
  //     'Content-Type': 'application/json',
  //     Authorization: `Bearer ${import.meta.env.VITE_OPENAI_API_KEY}`,
  //   },
  //   body: JSON.stringify({
  //     model: DefaultOpenAIModel,
  //     messages: [
  //       {
  //         role: 'system',
  //         content: systemPrompt,
  //       },
  //       {
  //         role: 'user',
  //         content: userMessage,
  //       },
  //     ],
  //     temperature: 0.1,
  //     max_tokens: 500,
  //   }),
  // })

  const response = await ApiService.Chatbot.GetIntents(
    userMessage,
    systemPrompt
  )

  // if (response.status == 200) {
  //   throw new Error(`OpenAI API error: ${response.status}`)
  // }

  const data = await response.data
  const result = JSON.parse(data.choices[0].message.content)

  return {
    intent: result.intent || 'other',
    entities: result.entities || {},
    confidence: result.confidence || 0.5,
  }
}
export const processUserMessage = async (
  message: string,
  context?: IntentContext
): Promise<IntentResult> => {
  try {
    // Step 1: Use OpenAI to analyze intent and entities
    const openAIResponse = await analyzeWithOpenAI(message)

    // Step 2: Find corresponding intent handler
    const intentDef = intentRegistry.getIntent(openAIResponse.intent)

    if (!intentDef) {
      throw new Error(`Unknown intent: ${openAIResponse.intent}`)
    }

    // Step 3: Validate confidence score
    if (openAIResponse.confidence < intentDef.confidence_threshold) {
      console.warn(
        `Low confidence for intent ${openAIResponse.intent}: ${openAIResponse.confidence}`
      )
    }

    // Step 4: Validate and format entities
    const validatedEntities = validateEntities(
      openAIResponse.entities,
      intentDef
    )

    // Step 5: Call intent handler to process
    const handlerResult = await intentDef.handler(
      validatedEntities,
      context || {},
      message
    )

    // Step 6: Create final result
    const finalResult: IntentResult = {
      intent: openAIResponse.intent,
      entities: validatedEntities,
      confidence: openAIResponse.confidence,
      response: handlerResult.response,
      data: handlerResult.data,
      actions: handlerResult.actions,
      template: handlerResult.template, // Add template from handler
    }

    return finalResult
  } catch (error) {
    console.error('Error in processUserMessage:', error)

    // Fallback to generic response
    const fallbackResult: IntentResult = {
      intent: 'other',
      entities: {},
      confidence: 0.3,
      response:
        "Sorry, I couldn't understand your request. Could you please clarify?",
    }
    return fallbackResult
  }
}
